/**
 * A quiet "learn more" popout.
 *
 * Explanatory small print clutters a screen and gets skipped anyway. This puts
 * it one tap away instead: a real button (44px tall, keyboard operable,
 * described to screen readers) that opens a spacious panel with the detail.
 */
import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface InfoPopoutProps {
  /** Visible trigger text. Keep it to two or three words. */
  label: string;
  /** Panel heading. Defaults to the label. */
  title?: string;
  children: ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  /** Hide the trigger glyph for very tight rows. */
  showIcon?: boolean;
}

export default function InfoPopout({
  label,
  title,
  children,
  align = "end",
  className = "",
  showIcon = true,
}: InfoPopoutProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={title ?? label}
          className={`inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${className}`}
        >
          {showIcon && <Info className="h-3.5 w-3.5" aria-hidden="true" />}
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-[min(22rem,calc(100vw-2rem))] rounded-3xl p-5"
      >
        <h3 className="font-display text-sm font-semibold">{title ?? label}</h3>
        <div className="mt-3 space-y-4 text-xs leading-relaxed text-muted-foreground">
          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
}
